import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Wind, Drop, CloudRain, ThermometerSimple, ShieldWarning, Crosshair } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

const STATUS_CONFIG = {
  good: { bg: 'bg-green-50 dark:bg-green-950/30', border: 'border-green-400 dark:border-green-700', badge: 'bg-green-200 text-green-900 dark:bg-green-800 dark:text-green-100', dot: 'bg-green-500' },
  marginal: { bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-400 dark:border-amber-700', badge: 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100', dot: 'bg-amber-500' },
  hold: { bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-400 dark:border-red-700', badge: 'bg-red-200 text-red-900 dark:bg-red-800 dark:text-red-100', dot: 'bg-red-500' },
  unknown: { bg: 'bg-muted', border: 'border-border', badge: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' },
};

const DISEASE_CONFIG = {
  low: { bg: 'bg-green-50 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-300', bar: 'bg-green-500', width: 'w-1/4' },
  moderate: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-300', bar: 'bg-amber-500', width: 'w-2/4' },
  high: { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-300', bar: 'bg-orange-500', width: 'w-3/4' },
  critical: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-300', bar: 'bg-red-500', width: 'w-full' },
};

export const WeatherIntelligence = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_URL}/api/weather/intelligence`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('location');
      } else {
        setError('fetch');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (error === 'location') return null;
  if (loading) {
    return (
      <div data-testid="weather-intelligence-loading" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-5 pb-4 h-36" />
          </Card>
        ))}
      </div>
    );
  }
  if (error || !data) return null;

  const { spray_window: sw, wind, rain, disease_risk: disease, water_balance: wb } = data;
  const swStyle = STATUS_CONFIG[sw.status] || STATUS_CONFIG.unknown;
  const disStyle = DISEASE_CONFIG[disease.level] || DISEASE_CONFIG.low;

  return (
    <div data-testid="weather-intelligence-section">
      <div className="flex items-center gap-2 mb-3">
        <ThermometerSimple size={18} weight="duotone" className="text-primary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Weather Intelligence</h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">

        {/* 1. Spray Window */}
        <Card data-testid="spray-window-card" className={`border-2 ${swStyle.border} ${swStyle.bg} md:col-span-2 lg:col-span-1`}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Crosshair size={20} weight="duotone" className="text-foreground" />
                <span className="font-semibold text-sm">Spray Window</span>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${swStyle.badge}`} data-testid="spray-window-badge">
                {sw.label}
              </span>
            </div>
            <div className="space-y-1.5">
              {sw.reasons.map((r, i) => (
                <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${swStyle.dot}`} />
                  {r}
                </p>
              ))}
            </div>
            {sw.best_window && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  Best window: <strong className="text-foreground">{sw.best_window.label}</strong>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. Wind */}
        <Card data-testid="wind-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <Wind size={20} weight="duotone" className="text-blue-500" />
              <span className="font-semibold text-sm">Wind</span>
            </div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className={`text-3xl font-bold ${
                wind.current_mph >= 12 ? 'text-red-600 dark:text-red-400' :
                wind.current_mph >= 8 ? 'text-amber-600 dark:text-amber-400' :
                'text-green-600 dark:text-green-400'
              }`}>
                {wind.current_mph}
              </span>
              <span className="text-sm text-muted-foreground">mph</span>
              <span className="text-xs text-muted-foreground ml-1">{wind.direction}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Gusts: <strong className={wind.gusts_mph >= 15 ? 'text-red-600 dark:text-red-400' : ''}>{wind.gusts_mph} mph</strong>
              <span className="ml-2 text-muted-foreground/60">Drift limit: 10 mph</span>
            </p>
            {/* 6-hour mini trend */}
            {wind.trend.length > 0 && (
              <div className="flex items-end gap-1 h-10">
                {wind.trend.slice(0, 8).map((t, i) => {
                  const max = Math.max(...wind.trend.slice(0, 8).map(x => x.wind_mph), 1);
                  const height = Math.max((t.wind_mph / max) * 100, 8);
                  const color = t.wind_mph >= 12 ? 'bg-red-400' : t.wind_mph >= 8 ? 'bg-amber-400' : 'bg-green-400';
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                      <div className={`w-full rounded-sm ${color}`} style={{ height: `${height}%` }} title={`${t.hour}: ${t.wind_mph}mph`} />
                      {i % 2 === 0 && <span className="text-[8px] text-muted-foreground/60">{t.hour}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. Rain Tracker */}
        <Card data-testid="rain-tracker-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <CloudRain size={20} weight="duotone" className="text-blue-400" />
              <span className="font-semibold text-sm">Rain Tracker</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Last 24h</p>
                <p className="text-xl font-bold">{rain.last_24h_in}"</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Next 48h</p>
                <p className="text-xl font-bold">{rain.next_48h_in}"</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                Rain chance (24h): <strong className={rain.max_probability_24h >= 60 ? 'text-blue-600' : ''}>{rain.max_probability_24h}%</strong>
              </p>
              {rain.next_rain_time ? (
                <p className="text-xs text-muted-foreground">
                  Next rain: <strong>{rain.next_rain_time}</strong> ({rain.next_rain_amount_in}")
                </p>
              ) : (
                <p className="text-xs text-green-600 dark:text-green-400 font-medium">No rain expected</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 4. Disease Risk */}
        <Card data-testid="disease-risk-card" className={disStyle.bg}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldWarning size={20} weight="duotone" className={disStyle.text} />
                <span className="font-semibold text-sm">Disease Risk</span>
              </div>
              <span className={`text-xs font-bold uppercase ${disStyle.text}`} data-testid="disease-risk-level">{disease.level}</span>
            </div>
            {/* Risk gauge bar */}
            <div className="w-full h-2 rounded-full bg-muted mb-3">
              <div className={`h-full rounded-full transition-all ${disStyle.bar} ${disStyle.width}`} />
            </div>
            <div className="space-y-1.5">
              {disease.threats.map((t, i) => (
                <p key={i} className="text-xs text-muted-foreground">{t}</p>
              ))}
            </div>
            {disease.humid_hours_24 > 0 && (
              <p className="text-[10px] text-muted-foreground/60 mt-2 italic">
                {disease.humid_hours_24}h of high humidity in last 24h
              </p>
            )}
          </CardContent>
        </Card>

        {/* 5. Water Balance */}
        <Card data-testid="water-balance-card" className="md:col-span-2 lg:col-span-1">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <Drop size={20} weight="duotone" className="text-cyan-500" />
              <span className="font-semibold text-sm">7-Day Water Balance</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-center">
                <p className="text-[10px] font-semibold uppercase text-muted-foreground">Rain In</p>
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{wb.total_rain_in}"</p>
              </div>
              <div className="text-center px-3">
                <p className="text-[10px] font-semibold uppercase text-muted-foreground">vs</p>
                <p className={`text-sm font-bold ${
                  wb.status === 'deficit' ? 'text-red-600 dark:text-red-400' :
                  wb.status === 'surplus' ? 'text-green-600 dark:text-green-400' :
                  'text-muted-foreground'
                }`}>
                  {wb.deficit_in > 0 ? '+' : ''}{wb.deficit_in}"
                </p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-semibold uppercase text-muted-foreground">ET Out</p>
                <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{wb.total_et_in}"</p>
              </div>
            </div>
            {/* Day-by-day mini bars */}
            <div className="flex items-end gap-1 h-14">
              {wb.days.map((day, i) => {
                const maxVal = Math.max(...wb.days.map(d => Math.max(d.rain_in, d.et_in)), 0.01);
                const rainH = Math.max((day.rain_in / maxVal) * 100, 2);
                const etH = Math.max((day.et_in / maxVal) * 100, 2);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="flex gap-px items-end w-full" style={{ height: '80%' }}>
                      <div className="flex-1 bg-blue-400 dark:bg-blue-500 rounded-sm" style={{ height: `${rainH}%` }} title={`Rain: ${day.rain_in}"`} />
                      <div className="flex-1 bg-orange-400 dark:bg-orange-500 rounded-sm" style={{ height: `${etH}%` }} title={`ET: ${day.et_in}"`} />
                    </div>
                    <span className="text-[8px] text-muted-foreground/60">{day.day}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-4 mt-1.5">
              <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2 h-2 rounded-sm bg-blue-400" />Rain</span>
              <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-2 h-2 rounded-sm bg-orange-400" />ET</span>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};
